import { Flex, Image } from "@mantine/core";
import { CITY, COUNTRY, COUNTRY_JP, FORM_ITEM_KEY, SCHOOL_KEY, VISA, VISA_OPTION } from "../constants/Common";
import { SystemCode } from "../redux/system-codes/type";
import { FormItem } from "../types/application";
import Modal from "../components/Modal/Modal";
import { JSX, useState } from "react";
import { SchoolDetails } from "../types/school-details";

export const isCountryUnderAmerica = (countryKey: string): boolean => {
  const countriesUnderAmerica: string[] = [COUNTRY.US_EAST, COUNTRY.US_WEST, COUNTRY.HAWAII, COUNTRY.AMERICA];
  return countriesUnderAmerica.includes(countryKey);
}

export const getFormTitle = (formItem: FormItem, cities: SystemCode[] | undefined, formCityValue: string | undefined): string => {
  if (formItem.formItemKey.includes(FORM_ITEM_KEY.QUESTION_SCHOOL)) {
    return getSchoolLabel(cities, formCityValue);
  } else {
    return formItem.title;
  }
}

const getSchoolLabel = (cities: SystemCode[] | undefined, formCityValue: string | undefined): string => {
  const city = cities?.find(option => option.key === formCityValue)?.japVal || '';
  return city + 'の語学学校';
}

export const isFormHidden = (formItem: FormItem, countryKey: string | undefined, visaTypeKey: string | undefined) => {
  const citiesWithoutHomestay = [COUNTRY.DUBAI, COUNTRY.SOUTH_AFRICA];
  return formItem.formItemKey === FORM_ITEM_KEY.QUESTION_UNCONFIRMED_START_DATE ||
    formItem.formItemKey.includes(FORM_ITEM_KEY.FINAL_CONFIRMATION) ||
    (formItem.formItemKey.includes(FORM_ITEM_KEY.QUESTION_HOMESTAY_ACCOMMODATION_TERMS) && citiesWithoutHomestay.includes(countryKey as COUNTRY)) ||
    (visaTypeKey === VISA_OPTION.NO_NEED && (formItem.formItemKey.includes(VISA.ETAS) || formItem.formItemKey.includes(VISA.STUDENT) || formItem.formItemKey.includes(VISA.WORKING))) ||
    (visaTypeKey === VISA_OPTION.ETAS && (formItem.formItemKey.includes(VISA.NO_NEED) || formItem.formItemKey.includes(VISA.STUDENT) || formItem.formItemKey.includes(VISA.WORKING))) ||
    (visaTypeKey === VISA_OPTION.STUDENT && (formItem.formItemKey.includes(VISA.NO_NEED) || formItem.formItemKey.includes(VISA.ETAS) || formItem.formItemKey.includes(VISA.WORKING))) ||
    (visaTypeKey === VISA_OPTION.WORKING && (formItem.formItemKey.includes(VISA.NO_NEED) || formItem.formItemKey.includes(VISA.STUDENT) || formItem.formItemKey.includes(VISA.ETAS))) ||
    (visaTypeKey === VISA_OPTION.ETAS && formItem.formItemKey === FORM_ITEM_KEY.QUESTION_USE_VISA_APPLICATION_BEHALF && COUNTRY.AUSTRALIA === countryKey) ||
    (!visaTypeKey?.includes(VISA_OPTION.STUDENT) && formItem.formItemKey === FORM_ITEM_KEY.QUESTION_VISA_STUDENT_CONFIRMATION_MALTA && COUNTRY.MALTA === countryKey);
}

export const getSchoolOptions = (options: SystemCode[], schoolDetails?: SchoolDetails[]) => {
  const schoolDetailsMap = new Map<string, SchoolDetails>();
  schoolDetails?.forEach((detail) => {
    schoolDetailsMap.set(detail.schoolKey, detail);
  });

  return options?.length ? options
    .map(option => { return {key: option.key, value: `${getSchoolRating(option.key, schoolDetailsMap)}${getSchoolName(option.key, option.japVal || option.value)}`}})
    .flatMap(option => option.key === SCHOOL_KEY.TORONTO_ENGLISH_PATH_TORONTO ?
        [
          {key: option.key + '_AM', value: 'English Path（AMプラン）'}
        ] :
        option)
    : options;
};

const getSchoolRating = (schoolKey: string, schoolDetailsMap: Map<string, SchoolDetails>) => {
  const details = schoolDetailsMap.get(schoolKey);
  if (!details) return "";
  let stars = "";
  if (details.hasBlackStar) stars += "★";
  if (details.hasWhiteStar) stars += "☆";
  return stars;
}

const getSchoolName = (schoolKey: string, schoolName: string) => {
  return schoolKey === SCHOOL_KEY.DUBLIN_CENTRE_OF_ENGLISH_STUDIES_DUBLIN ? 'Centre of English Studies※9,000円/週の追加費用' : schoolName;
}

export const getImageComponent = (formItem: FormItem, isImageModalOpen: boolean, setImageModalOpen: React.Dispatch<React.SetStateAction<boolean>>, alignLeft = false): JSX.Element => {
  return <Flex flex={1} justify={alignLeft ? 'start' : 'center'} align="center">
    <Image style={{ cursor: "pointer" }} src={formItem.fileUrl || getImage(formItem.formItemKey)} alt="" maw={300} onClick={() => setImageModalOpen(true)} />
    <Modal isOpen={isImageModalOpen} closeModal={() => setImageModalOpen(false)} title="" size="1000px">
      <Image src={formItem.fileUrl || getImage(formItem.formItemKey)} alt="" />
    </Modal>
  </Flex>
}

const getImage = (formItemKey: string): string => {
  switch (formItemKey) {
    case FORM_ITEM_KEY.QUESTION_SAMPLE: return '/images/preview.png';
    case FORM_ITEM_KEY.PEACE_OF_MIND_SAMPLE: return '/images/peace-of-mind.png';
    default: return '';
  }
}

export const getTextCondition = (formItem: FormItem, cityKey: string | undefined): string => {
  switch (formItem.formItemKey) {
    case FORM_ITEM_KEY.QUESTION_SCHOOL:
      return getSchoolConditionText(formItem, cityKey);
    default: return formItem.text;
  }
}

const getSchoolConditionText = (formItem: FormItem, cityKey: string | undefined): string => {
  const citiesWithStarAustralia: string[] = [CITY.MELBOURNE, CITY.GOLD_COAST, CITY.BRISBANE];
  const citiesWithStarsUK: string[] = [CITY.LONDON];
  if (cityKey === undefined) {
    return formItem.text;
  } else if (citiesWithStarAustralia.includes(cityKey)) {
    return '※就労確約プランでご契約されたお客様は★がついている語学学校のみ選択可能でござい ます。<br/>※就労確約プラン (PM) でご契約されたお客様は☆がついている語学学校のみ選択可能で ございます。';
  } else if (citiesWithStarsUK.includes(cityKey)) {
    return '就学週数が36週または44週の場合は★がついている学校のみ選択可能です。';
  } else {
    return formItem.text;
  }
}

export const getFullAddress = (basicInfo: any): string => {
  if (basicInfo) {
    const address1: string = basicInfo.address1Prefecture || '';
    const address2: string = basicInfo.address2Municipality || '';
    const address3: string = basicInfo.address3HouseNumber || '';
    const address4: string = basicInfo.address4BuildingName || '';

    if (address2 !== null && (address2.includes(address1) && address2.includes(address3) && address2.includes(address4))) {
      return address2;
    } else {
      return address1 + address2 + address3 + address4;
    }
  } else {
    return '';
  }
}

export const getStudyAbroadTitle = (rawCountry: string | undefined) => {
  const country = getActualCountryName(rawCountry).replace(/東部|西部/g, '') || '';
  return `【${country}留学】ご留学内容確定フォーム`;
}

const getActualCountryName = (country: string | undefined): string => {
  return Object.values(COUNTRY_JP).find((value: string) => value === country) !== undefined ? COUNTRY_JP.AMERICA : country || '';
}
